import { getOr, isFunction, isNil } from 'lodash/fp';
import { debounce } from 'app/lib/fp';
import {
    replaceProjectSlug,
    navigateToHome,
    navigateToWorkbench,
    isWorkbench,
    isProject,
    isProjectsList,
    navigateToHomePageTable,
    isMigrationWorkbenchPage
} from 'app/routes';

import { doInvalidateDownstreamDatasets } from 'app/store/datasetData/actions.js';
import { selectView } from 'app/store/view/selectors.js';
import { doListUserDetails } from 'app/store/users';

import { doLoadProject, doLoadProjects } from 'app/store/projects/actions.js';
import { isCurrentProject, selectCurrentProjectId } from 'app/store/projects/selectors.js';
import { selectCurrentUserName } from 'app/store/users/selectors.js';
import { doGetAccessControls } from 'app/store/accessControls/actions.js';
import { doLoadGlossary } from 'app/store/glossary/actions.js';
import { doValidateSnowflakeAccess } from 'app/store/access/actions';
import { doGetSnowflakeObjectsInSchema } from 'app/store/snowflake';
import { selectIsSourceLocationByDatabaseAndSchemaEmpty } from 'app/store/assets/selectors.js';
import { doGetMigrationFiles, selectSelectedMigrationId } from 'app/store/migration';
import { doExecuteFQNQuery, doDeleteFQNQuery } from 'app/store/snowflakeQueries/actions.js';
import {
    DELETE_TRANSFORMATION_SUCCESS_TRANSFORMATION_DELETED,
    CREATE_BLEND_SUCCESS_TRANSFORMATION_CREATED,
    UPDATE_BLEND_SUCCESS_TRANSFORMATION_UPDATED,
    CREATE_FILTER_SUCCESS_TRANSFORMATION_CREATED,
    UPDATE_FILTER_SUCCESS_TRANSFORMATION_UPDATED,
    CREATE_PROJECTION_SUCCESS_TRANSFORMATION_CREATED,
    UPDATE_PROJECTION_SUCCESS_TRANSFORMATION_UPDATED,
    CREATE_REPLACE_SUCCESS_TRANSFORMATION_CREATED,
    UPDATE_REPLACE_SUCCESS_TRANSFORMATION_UPDATED,
    CREATE_SPLIT_SUCCESS_TRANSFORMATION_CREATED,
    UPDATE_SPLIT_SUCCESS_TRANSFORMATION_UPDATED,
    CREATE_SUBSTRING_EXTRACT_SUCCESS_TRANSFORMATION_CREATED,
    UPDATE_SUBSTRING_EXTRACT_SUCCESS_TRANSFORMATION_UPDATED,
    DELETE_PROJECT_SUCCESS_PROJECT_DELETED,
    UPDATE_PROJECT_NAME_SUCCESS_PROJECT_NAME_UPDATED,
    CREATE_VIEW_SUCCESS_TRANSFORMATION_STACK_CREATED,
    ADD_COLLABORATORS_SUCCESS_COLLABORATORS_ADDED,
    REMOVE_COLLABORATORS_SUCCESS_COLLABORATORS_REMOVED,
    IMPORT_VIEW_SUCCESS_DATA_SOURCE_IMPORTED,
    UPDATE_USER_SUCCESS_USER_UPDATED,
    DISABLE_USER_SUCCESS_USER_DISABLED,
    UPDATE_PROJECT_OWNER_SUCCESS_PROJECT_OWNER_UPDATED,
    DELETE_GLOSSARY_CATEGORY_SUCCESS_GLOSSARY_CATEGORY_DELETED,
    UPDATE_GLOSSARY_CATEGORY_NAME_SUCCESS_GLOSSARY_CATEGORY_NAME_UPDATED,
    UPDATE_SQL_VIEW_SUCCESS_SQL_VIEW_UPDATED,
    CREATE_SQL_VIEW_SUCCESS_SQL_VIEW_CREATED,
    CREATE_FORMULA_SUCCESS_TRANSFORMATION_CREATED,
    UPDATE_FORMULA_SUCCESS_TRANSFORMATION_UPDATED,
    CREATE_AGGREGATION_SUCCESS_TRANSFORMATION_CREATED,
    UPDATE_AGGREGATION_SUCCESS_TRANSFORMATION_UPDATED,
    CREATE_PIVOT_SUCCESS_TRANSFORMATION_CREATED,
    UPDATE_PIVOT_SUCCESS_TRANSFORMATION_UPDATED,
    CREATE_SNOWFLAKE_TABLE_FOR_UPLOAD_SUCCESS_SNOWFLAKE_TABLE_FOR_UPLOAD_CREATED,
    EXCHANGE_SOURCE_VIEW_SUCCESS_SOURCE_VIEW_EXCHANGED,
    ADD_DAP_EXPORT_FILE_TO_MIGRATION_SUCCESS_DAP_EXPORT_FILE_IMPORTED,
    SET_MODEL_SUCCESS_MODEL_UPDATED,
    COMPLETE_JOB_RUN_SUCCESS_JOB_RUN_COMPLETED,
    MODEL_REFERENCES_UPDATED
} from 'app/store/__generated__/events';

import { isRedirectAllowed, toStringFqn } from 'app/lib/utils';

const isActorCurrentUser = ({ performedBy }, state) => selectCurrentUserName(state) === performedBy;

const loadSpace = debounce(
    { leading: false, trailing: true, maxWait: 5000 },
    500,
    (dispatch, spaceId) => dispatch(doLoadProject({ projectSlug: spaceId }))
);

function handleSpaceDeleted({ action, store }) {
    if (isRedirectAllowed(action.sessionId) && !isProjectsList()) {
        navigateToHome();
    }

    store.dispatch(doLoadProjects());
}

function handleCollaboratorsRemoved({ action, store }) {
    const state = store.getState();
    const currentUser = selectCurrentUserName(state);

    if (action.payload.collaborators.includes(currentUser)) {
        if (isRedirectAllowed(action.sessionId)) {
            navigateToHome();
        }
    } else {
        store.dispatch(doGetAccessControls({ entityIds: [action.payload.entityId] }));
    }
}

function updateSpaceName({ action, store }) {
    const state = store.getState();
    const { slug, assetId } = action.payload;

    if (isProject() && isCurrentProject(assetId, state)) {
        replaceProjectSlug(slug);
    }
}

const reloadSpaceIfisCurrentProject = (dispatch, state, spaceUid) => {
    if (isProject() && isCurrentProject(spaceUid, state)) {
        loadSpace(dispatch, spaceUid);
    }
};

const getViewRefFromOperationPayload = (payload) =>
    getOr(getOr(null, 'viewRef', payload), 'operation.viewRef', payload);

const invalidateDownstreamIfCurrentProject = (dispatch, state, spaceUid, viewUid) => {
    if (isProject() && isCurrentProject(spaceUid, state)) {
        dispatch(doInvalidateDownstreamDatasets({ ids: [viewUid] }));
    }
};

const handleExchangeSource = ({ action, store }) => {
    const state = store.getState();
    const { targetView } = action.payload;
    const [spaceUid, viewUid] = targetView.split('.');

    invalidateDownstreamIfCurrentProject(store.dispatch, state, spaceUid, viewUid);
};

export function createOperation({ action, store }) {
    const state = store.getState();
    const viewRef = getViewRefFromOperationPayload(action.payload);

    reloadSpaceIfisCurrentProject(store.dispatch, state, viewRef.spaceUid);
    invalidateDownstreamIfCurrentProject(store.dispatch, state, viewRef.spaceUid, viewRef.viewUid);
}

function updateOperation({ action, store }) {
    const state = store.getState();
    const viewRef = getViewRefFromOperationPayload(action.payload);
    const { spaceUid, viewUid } = viewRef;

    reloadSpaceIfisCurrentProject(store.dispatch, state, spaceUid);
    invalidateDownstreamIfCurrentProject(store.dispatch, state, spaceUid, viewUid);
}

function deleteOperation({ action, store }) {
    const state = store.getState();
    const viewRef = getViewRefFromOperationPayload(action.payload);

    reloadSpaceIfisCurrentProject(store.dispatch, state, viewRef.spaceUid);
    invalidateDownstreamIfCurrentProject(store.dispatch, state, viewRef.spaceUid, viewRef.viewUid);
}

function handleViewCreated({ action, store }) {
    const state = store.getState();
    const { spaceSlug, spaceUid, viewUid } = action.payload.viewRef;
    const actorIsCurrentUser = isActorCurrentUser(action.payload, state);

    reloadSpaceIfisCurrentProject(store.dispatch, state, spaceUid);
    invalidateDownstreamIfCurrentProject(store.dispatch, state, spaceUid, viewUid);

    if (
        actorIsCurrentUser &&
        isRedirectAllowed(action.sessionId) &&
        !action.payload.meta?.cancelRedirect &&
        !state?.sqlEditor?.keepOpen
    ) {
        navigateToWorkbench(spaceSlug, viewUid);
    }
}

function handleReloadUser({ store }) {
    store.dispatch(doListUserDetails());
}

function handleAssetReferenced({ action, store }) {
    const state = store.getState();
    const canRedirect = isRedirectAllowed(action.sessionId);
    const { spaceSlug, spaceUid, viewUid } = action.payload?.viewRef ?? {};
    const inCurrentProject = isCurrentProject(spaceUid, state);
    const shouldRedirect = !isWorkbench() || (isWorkbench() && inCurrentProject);

    if (canRedirect && shouldRedirect) {
        navigateToWorkbench(spaceSlug, viewUid);
    }

    if (inCurrentProject) {
        loadSpace(store.dispatch, spaceUid);
    }
}

function handleOwnerUpdate({ action, store }) {
    store.dispatch(
        doGetAccessControls({ entityIds: [action.payload.entityId ?? action.payload.assetId] })
    );
    store.dispatch(
        doLoadProject({ projectSlug: action.payload.entityId ?? action.payload.assetId })
    );
}

function reloadGlossary({ store }) {
    store.dispatch(doLoadGlossary());
}

function handleUploadSuccess({ action, store }) {
    const { database, schema, table, spaceUid } = action.payload;
    const snowflakeTableFqn = { database, schema, table };
    const isLocationEmpty = selectIsSourceLocationByDatabaseAndSchemaEmpty(store.getState(), {
        database,
        schema
    });
    if (!isLocationEmpty) {
        store.dispatch(
            doGetSnowflakeObjectsInSchema({ schemaFqn: toStringFqn({ database, schema }) })
        );
    }
    store.dispatch(doValidateSnowflakeAccess([snowflakeTableFqn]));

    if (isNil(spaceUid) && isRedirectAllowed(action.sessionId)) {
        navigateToHomePageTable(toStringFqn(snowflakeTableFqn));
    }
}

function handleDapExportUpload({ action, store }) {
    const state = store.getState();
    const { migrationUid } = action.payload;
    const selectedMigrationUid = selectSelectedMigrationId(state);

    if (isMigrationWorkbenchPage() && selectedMigrationUid === migrationUid) {
        store.dispatch(doGetMigrationFiles(migrationUid));
    }
}

function handleJobRunCompleted({ action, store }) {
    if (isProject()) {
        const payload = action.payload;
        const view = selectView(store.getState());
        const modelId = payload?.modelRef?.id;
        const isViewingModel = view?.id === modelId;

        if (isViewingModel) {
            store.dispatch(
                doExecuteFQNQuery({
                    fqn: payload?.targetTable
                })
            );
        } else {
            store.dispatch(
                doDeleteFQNQuery({
                    fqn: payload?.targetTable
                })
            );
        }

        store.dispatch(doInvalidateDownstreamDatasets({ ids: [`_v${modelId}`] }));
    }
}

function handleModelReferencesUpdated({ store }) {
    const currentProjectUid = selectCurrentProjectId(store.getState());
    if (currentProjectUid) {
        loadSpace(store.dispatch, currentProjectUid);
    }
}

// TODO: Get rid of the CREATED variants for the ones we handle elsewhere
const EFFECTS = {
    [DELETE_TRANSFORMATION_SUCCESS_TRANSFORMATION_DELETED]: deleteOperation,
    [SET_MODEL_SUCCESS_MODEL_UPDATED]: updateOperation,
    [CREATE_BLEND_SUCCESS_TRANSFORMATION_CREATED]: createOperation,
    [UPDATE_BLEND_SUCCESS_TRANSFORMATION_UPDATED]: updateOperation,
    [CREATE_FILTER_SUCCESS_TRANSFORMATION_CREATED]: createOperation,
    [UPDATE_FILTER_SUCCESS_TRANSFORMATION_UPDATED]: updateOperation,
    [CREATE_PROJECTION_SUCCESS_TRANSFORMATION_CREATED]: createOperation,
    [UPDATE_PROJECTION_SUCCESS_TRANSFORMATION_UPDATED]: updateOperation,
    [CREATE_REPLACE_SUCCESS_TRANSFORMATION_CREATED]: createOperation,
    [UPDATE_REPLACE_SUCCESS_TRANSFORMATION_UPDATED]: updateOperation,
    [CREATE_SPLIT_SUCCESS_TRANSFORMATION_CREATED]: createOperation,
    [UPDATE_SPLIT_SUCCESS_TRANSFORMATION_UPDATED]: updateOperation,
    [CREATE_SUBSTRING_EXTRACT_SUCCESS_TRANSFORMATION_CREATED]: createOperation,
    [UPDATE_SUBSTRING_EXTRACT_SUCCESS_TRANSFORMATION_UPDATED]: updateOperation,
    [CREATE_FORMULA_SUCCESS_TRANSFORMATION_CREATED]: createOperation,
    [UPDATE_FORMULA_SUCCESS_TRANSFORMATION_UPDATED]: updateOperation,
    [CREATE_AGGREGATION_SUCCESS_TRANSFORMATION_CREATED]: createOperation,
    [UPDATE_AGGREGATION_SUCCESS_TRANSFORMATION_UPDATED]: updateOperation,
    [CREATE_PIVOT_SUCCESS_TRANSFORMATION_CREATED]: createOperation,
    [UPDATE_PIVOT_SUCCESS_TRANSFORMATION_UPDATED]: updateOperation,
    [DELETE_PROJECT_SUCCESS_PROJECT_DELETED]: handleSpaceDeleted,
    [UPDATE_PROJECT_NAME_SUCCESS_PROJECT_NAME_UPDATED]: updateSpaceName,
    [CREATE_VIEW_SUCCESS_TRANSFORMATION_STACK_CREATED]: handleViewCreated,
    [ADD_COLLABORATORS_SUCCESS_COLLABORATORS_ADDED]: handleOwnerUpdate,
    [REMOVE_COLLABORATORS_SUCCESS_COLLABORATORS_REMOVED]: handleCollaboratorsRemoved,
    [IMPORT_VIEW_SUCCESS_DATA_SOURCE_IMPORTED]: handleAssetReferenced,
    [UPDATE_USER_SUCCESS_USER_UPDATED]: handleReloadUser,
    [DISABLE_USER_SUCCESS_USER_DISABLED]: handleReloadUser,
    [UPDATE_PROJECT_OWNER_SUCCESS_PROJECT_OWNER_UPDATED]: handleOwnerUpdate,
    [DELETE_GLOSSARY_CATEGORY_SUCCESS_GLOSSARY_CATEGORY_DELETED]: reloadGlossary,
    [UPDATE_GLOSSARY_CATEGORY_NAME_SUCCESS_GLOSSARY_CATEGORY_NAME_UPDATED]: reloadGlossary,
    [UPDATE_SQL_VIEW_SUCCESS_SQL_VIEW_UPDATED]: handleViewCreated,
    [CREATE_SQL_VIEW_SUCCESS_SQL_VIEW_CREATED]: handleViewCreated,
    [CREATE_SNOWFLAKE_TABLE_FOR_UPLOAD_SUCCESS_SNOWFLAKE_TABLE_FOR_UPLOAD_CREATED]:
        handleUploadSuccess,
    [EXCHANGE_SOURCE_VIEW_SUCCESS_SOURCE_VIEW_EXCHANGED]: handleExchangeSource,
    [ADD_DAP_EXPORT_FILE_TO_MIGRATION_SUCCESS_DAP_EXPORT_FILE_IMPORTED]: handleDapExportUpload,
    [COMPLETE_JOB_RUN_SUCCESS_JOB_RUN_COMPLETED]: handleJobRunCompleted,
    [MODEL_REFERENCES_UPDATED]: handleModelReferencesUpdated
};

export const handleServerSentMessage = (action) => {
    const effect = EFFECTS[action.type];

    if (isFunction(effect)) {
        return { ...action, effect };
    }

    return action;
};
